import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useThemeTokens } from '../../design/useThemeTokens';
import { useReducedMotion } from '../../animations';
import { useRenderGate } from '../../lib/useRenderGate';
import { euclid, rotate, RhythmVoice, A3, RATIO } from '../../lib/euclid';

export interface ResonanceFinaleProps {
    voices?: RhythmVoice[];
    /** Cycle length in seconds — one full revolution of the playhead */
    cycleSeconds?: number;
    height?: number;
    children?: React.ReactNode;
}

/**
 * The default ensemble. Each voice is one integer pair, and each integer pair
 * is a rhythm that already exists somewhere in the world.
 */
/* Colours are resolved from the theme at draw time — a canvas cannot use
   var(), and the dark-mode ring colours are invisible on paper. */
const RING_TOKENS = ['--ring-1', '--ring-2', '--ring-3', '--ring-4', '--ring-5'];

const DEFAULT_VOICES: RhythmVoice[] = [
    { steps: 8,  pulses: 3, rotation: 0, freq: A3 * RATIO.octave, color: '#8be9fd', name: 'E(3,8) · tresillo' },
    { steps: 8,  pulses: 5, rotation: 0, freq: A3 * RATIO.fifth,  color: '#bd93f9', name: 'E(5,8) · cinquillo' },
    { steps: 12, pulses: 5, rotation: 0, freq: A3 * RATIO.fourth, color: '#50fa7b', name: 'E(5,12) · Venda' },
    { steps: 16, pulses: 7, rotation: 0, freq: A3 * RATIO.majorThird, color: '#ff79c6', name: 'E(7,16) · samba' },
    { steps: 5,  pulses: 2, rotation: 0, freq: A3,                 color: '#ffb86c', name: 'E(2,5) · khafif' },
];

const mono: React.CSSProperties = {
    fontFamily: "'JetBrains Mono','Fira Code',monospace",
};

/**
 * ResonanceFinale — where the flight lands.
 *
 * Concentric rings, one per voice, each carrying a Euclidean rhythm. A single
 * playhead sweeps all of them at once, so the rings drift in and out of phase
 * the way a polyrhythm does. When two rings fire on the same sweep, a chord
 * line is drawn between them — the visual is the alignment, not decoration.
 *
 * Silent until asked. A portfolio that makes noise at you unprompted is a
 * portfolio you close.
 */
const ResonanceFinale: React.FC<ResonanceFinaleProps> = ({
    voices = DEFAULT_VOICES,
    cycleSeconds = 6,
    height = 560,
    children,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const audioRef = useRef<AudioContext | null>(null);
    const lastStepRef = useRef(-1);
    const reduced = useReducedMotion();
    const shouldRender = useRenderGate(canvasRef);
    const [sound, setSound] = useState(false);
    const soundRef = useRef(false);
    soundRef.current = sound;
    const [mutedVoices, setMutedVoices] = useState<Record<string, boolean>>({});
    const mutedVoicesRef = useRef<Record<string, boolean>>({});
    mutedVoicesRef.current = mutedVoices;

    // Swap each voice's colour for its themed equivalent once the tokens resolve.
    const resolvedTokens = useThemeTokens(...RING_TOKENS, '--bs-ink');
    const ringColours = resolvedTokens.slice(0, RING_TOKENS.length);
    const playheadInk = resolvedTokens[RING_TOKENS.length] || '#fafafa';
    const themedVoices = React.useMemo(
        () => voices.map((v, i) => (ringColours[i] ? { ...v, color: ringColours[i] } : v)),
        [voices, ringColours],
    );

    // Patterns are pure data — compute once.
    const patterns = React.useMemo(
        () => themedVoices.map(v => rotate(euclid(v.pulses, v.steps), v.rotation ?? 0)),
        [themedVoices]
    );

    const ping = useCallback((freq: number) => {
        const ctx = audioRef.current;
        if (!ctx) return;
        const t = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.value = freq;
        // short percussive envelope; no sustain, so overlapping voices stay legible
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.09, t + 0.008);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.34);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.36);
    }, []);

    const toggleSound = useCallback(() => {
        if (!sound) {
            // AudioContext must be created inside a user gesture
            if (!audioRef.current) {
                const Ctor = window.AudioContext
                    || (window as any).webkitAudioContext;
                if (Ctor) audioRef.current = new Ctor();
            }
            audioRef.current?.resume?.();
        }
        setSound(s => !s);
    }, [sound]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let raf = 0;
        let w = 0, h = 0;
        const start = performance.now();

        const resize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const rect = canvas.getBoundingClientRect();
            w = rect.width; h = rect.height;
            canvas.width = Math.round(w * dpr);
            canvas.height = Math.round(h * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        resize();
        window.addEventListener('resize', resize);

        // The longest cycle sets the master period; everything else is a factor
        // of it, which is what makes the whole figure repeat rather than drift.
        const master = themedVoices.reduce((m, v) => Math.max(m, v.steps), 1);

        const frame = (now: number) => {
            if (!shouldRender.current) { raf = requestAnimationFrame(frame); return; }
            const elapsed = reduced ? 0 : (now - start) / 1000;
            const phase = (elapsed / cycleSeconds) % 1;
            const cx = w / 2, cy = h / 2;
            const maxR = Math.min(w, h) * 0.42;

            ctx.clearRect(0, 0, w, h);

            const step = Math.floor(phase * master);
            const fireThisFrame = step !== lastStepRef.current;
            if (fireThisFrame) lastStepRef.current = step;

            const firing: { x: number; y: number; c: string }[] = [];

            themedVoices.forEach((v, vi) => {
                const isMuted = Boolean(mutedVoicesRef.current[v.name]);
                const pattern = patterns[vi];
                const r = maxR * (0.34 + (vi / Math.max(1, themedVoices.length - 1)) * 0.66);

                // the ring itself
                ctx.beginPath();
                ctx.arc(cx, cy, r, 0, Math.PI * 2);
                ctx.strokeStyle = isMuted ? `${v.color}0d` : `${v.color}1f`;
                ctx.lineWidth = 1;
                ctx.stroke();
                if (isMuted) return;

                const stepInVoice = Math.floor(phase * v.steps) % v.steps;

                for (let i = 0; i < v.steps; i++) {
                    const ang = (i / v.steps) * Math.PI * 2 - Math.PI / 2;
                    const x = cx + Math.cos(ang) * r;
                    const y = cy + Math.sin(ang) * r;
                    const onset = pattern[i];

                    if (!onset) {
                        ctx.beginPath();
                        ctx.arc(x, y, 1.6, 0, Math.PI * 2);
                        ctx.fillStyle = `${v.color}33`;
                        ctx.fill();
                        continue;
                    }

                    // how recently the playhead passed this onset, 1 -> 0
                    const d = ((phase * v.steps) - i + v.steps) % v.steps;
                    const hot = reduced ? 0.45 : Math.max(0, 1 - d / 1.1);
                    const rad = 3.2 + hot * 7;

                    if (hot > 0.75) firing.push({ x, y, c: v.color });

                    if (hot > 0.02) {
                        ctx.beginPath();
                        ctx.arc(x, y, rad + 9 * hot, 0, Math.PI * 2);
                        ctx.fillStyle = `${v.color}${Math.round(hot * 34).toString(16).padStart(2, '0')}`;
                        ctx.fill();
                    }
                    ctx.beginPath();
                    ctx.arc(x, y, rad, 0, Math.PI * 2);
                    ctx.fillStyle = v.color;
                    ctx.globalAlpha = 0.45 + hot * 0.55;
                    ctx.fill();
                    ctx.globalAlpha = 1;

                    if (fireThisFrame && soundRef.current && !reduced
                        && stepInVoice === i) {
                        ping(v.freq);
                    }
                }
            });

            // chord lines: rings agreeing on this beat
            for (let i = 0; i < firing.length; i++) {
                for (let j = i + 1; j < firing.length; j++) {
                    ctx.beginPath();
                    ctx.moveTo(firing[i].x, firing[i].y);
                    ctx.lineTo(firing[j].x, firing[j].y);
                    ctx.strokeStyle = `${firing[i].c}44`;
                    ctx.lineWidth = 1;
                    ctx.stroke();
                }
            }

            // playhead
            if (!reduced) {
                const a = phase * Math.PI * 2 - Math.PI / 2;
                const grad = ctx.createLinearGradient(
                    cx, cy, cx + Math.cos(a) * maxR, cy + Math.sin(a) * maxR);
                grad.addColorStop(0, 'rgba(0,0,0,0)');
                grad.addColorStop(1, `${playheadInk}80`);
                ctx.beginPath();
                ctx.moveTo(cx, cy);
                ctx.lineTo(cx + Math.cos(a) * maxR * 1.04, cy + Math.sin(a) * maxR * 1.04);
                ctx.strokeStyle = grad;
                ctx.lineWidth = 1.4;
                ctx.stroke();
            }

            raf = requestAnimationFrame(frame);
        };

        raf = requestAnimationFrame(frame);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', resize);
        };
    }, [themedVoices, patterns, cycleSeconds, reduced, ping, shouldRender, playheadInk]);

    // stop the oscillator graph when the tab is hidden
    useEffect(() => {
        const onVis = () => {
            if (document.hidden) audioRef.current?.suspend?.();
            else if (soundRef.current) audioRef.current?.resume?.();
        };
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, []);

    return (
        <div style={{ position: 'relative' }}>
            <canvas
                ref={canvasRef}
                aria-hidden
                style={{ width: '100%', height, display: 'block' }}
            />

            <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'none', textAlign: 'center', padding: '0 24px',
            }}>
                {children}
            </div>

            {/* the legend is interactive: each ring is an integer pair you can toggle */}
            <div role="group" aria-label="Euclidean rhythm voices" style={{
                display: 'flex', flexWrap: 'wrap', gap: '10px 14px',
                justifyContent: 'center', alignItems: 'center', marginTop: '18px',
            }}>
                {themedVoices.map(v => {
                    const activeVoice = !mutedVoices[v.name];
                    return (
                        <button
                            key={v.name}
                            type="button"
                            className="tap-hit"
                            aria-pressed={activeVoice}
                            aria-label={`${activeVoice ? 'Mute' : 'Unmute'} ${v.name}`}
                            onClick={() => {
                                setMutedVoices(prev => {
                                    const nextMuted = !prev[v.name];
                                    if (!nextMuted && soundRef.current) ping(v.freq);
                                    return { ...prev, [v.name]: nextMuted };
                                });
                            }}
                            style={{
                                ...mono, fontSize: '11px', color: activeVoice ? v.color : 'var(--bs-dim)',
                                background: 'transparent',
                                border: `1px solid ${activeVoice ? 'var(--bs-rule)' : 'transparent'}`,
                                padding: '5px 10px', cursor: 'pointer',
                                opacity: activeVoice ? 1 : 0.5,
                                display: 'inline-flex', alignItems: 'center', gap: '7px',
                                transition: 'opacity 150ms var(--bs-ease), border-color 150ms var(--bs-ease)',
                            }}
                        >
                            <span style={{
                                width: 7, height: 7, borderRadius: '50%',
                                background: activeVoice ? v.color : 'var(--bs-dim)', display: 'inline-block',
                            }} />
                            {v.name}
                        </button>
                    );
                })}
                <button
                    type="button"
                    className="tap-hit"
                    onClick={toggleSound}
                    aria-pressed={sound}
                    aria-label={sound ? 'Turn sound off' : 'Turn sound on'}
                    style={{
                        ...mono, fontSize: '11px', cursor: 'pointer',
                        background: sound ? 'var(--bs-accent-soft)' : 'transparent',
                        border: `1px solid ${sound ? 'var(--bs-accent)' : 'var(--bs-rule-strong)'}`,
                        color: sound ? 'var(--bs-accent)' : 'var(--bs-muted)',
                        borderRadius: '8px', padding: '5px 12px',
                    }}
                >
                    {sound ? '♪ sound on' : '♪ sound off'}
                </button>
            </div>
        </div>
    );
};

export default ResonanceFinale;
